/**
 * Config for the mega-menu FeaturePage live experience.
 *
 * Edit this file to change:
 *  - the rotating "LIVE" ticker events
 *  - the animated KPI counter targets, labels, growth badges
 *  - refresh/rotation cadence
 *
 * No other code changes are needed — FeaturePage reads from here.
 */

export type LiveEvent = {
  /** City shown in bold before the event text. */
  city: string;
  /** Human-readable event, e.g. "Dr. Rahman just signed a prescription". */
  text: string;
};

export type LiveKpiKey = "rx" | "patients" | "revenue" | "chambers";

export type LiveKpi = {
  key: LiveKpiKey;
  /** Short visible label under the number. */
  label: string;
  /** Full sentence used for screen-reader announcements. */
  spoken: (value: number) => string;
  /** Badge in the corner ("+18%", "24/7", …). */
  delta: string;
  /** Icon name from lucide-react. */
  icon: "FileCheck2" | "Users" | "Zap" | "Globe2";
  /** Base value shown on first paint. */
  baseValue: number;
  /** Value increment applied per auto-refresh tick (0 = static). */
  perTickIncrement: number;
  /** Optional formatter for the visible number, defaults to toLocaleString. */
  format?: (n: number) => string;
};

export type LiveActivityConfig = {
  /** Rotating events in the top LIVE bar. */
  events: LiveEvent[];
  /** KPI cards in the "Live across Bangladesh" section. */
  kpis: LiveKpi[];
  /** Ticker: ms between event rotations. */
  eventRotationMs: number;
  /** Counters: ms between auto-increment ticks. */
  kpiRefreshMs: number;
  /** Counters: count-up animation duration in ms. */
  countUpDurationMs: number;
};

export const LIVE_ACTIVITY: LiveActivityConfig = {
  eventRotationMs: 3200,
  kpiRefreshMs: 4500,
  countUpDurationMs: 1400,

  events: [
    { city: "Dhaka", text: "Dr. Rahman just signed a prescription" },
    { city: "Chittagong", text: "New patient checked in via kiosk" },
    { city: "Sylhet", text: "Follow-up SMS sent to 12 patients" },
    { city: "Khulna", text: "৳ 4,200 collected via bKash" },
    { city: "Rajshahi", text: "AI flagged a high-risk cardiac patient" },
    { city: "Dhaka", text: "Vaccine due reminder recalled 3 children" },
  ],

  kpis: [
    {
      key: "rx",
      label: "Prescriptions signed today",
      spoken: (n) => `${n.toLocaleString()} prescriptions signed today`,
      delta: "+18%",
      icon: "FileCheck2",
      baseValue: 1284,
      perTickIncrement: 3,
    },
    {
      key: "patients",
      label: "Patients seen this week",
      spoken: (n) => `${n.toLocaleString()} patients seen this week`,
      delta: "+9%",
      icon: "Users",
      baseValue: 942,
      perTickIncrement: 1,
    },
    {
      key: "revenue",
      label: "Revenue tracked today",
      spoken: (n) => `${n.toLocaleString()} taka in revenue tracked today`,
      delta: "+22%",
      icon: "Zap",
      baseValue: 487200,
      perTickIncrement: 250,
      format: (n) => `৳ ${n.toLocaleString()}`,
    },
    {
      key: "chambers",
      label: "Chambers running live",
      spoken: (n) => `${n} chambers running live`,
      delta: "24/7",
      icon: "Globe2",
      baseValue: 63,
      perTickIncrement: 0,
    },
  ],
};
